import React from "react";
import { useDebounce } from "use-debounce";

import type { RouterOutput } from "@lattice/backend/trpc";
import {
  MixpanelEvents,
  trackEvent,
} from "@lattice/frontend/lib/analytics/track";
import { trpcApi } from "@lattice/frontend/lib/trpc/client/index";

import {
  StockSearchView,
  type T_AutocompleteSelectedItem,
} from "./StockSearchView";

interface T_StockSearchProps {
  appearance?: "default" | "fill";
  className?: string;
  fadingStocks?: Set<string>;
  inlineOptions?: boolean;
  onClearSelectedItems?: () => void;
  onCloseSidebar?: () => void;
  /**
   * Observe the typed text while search state stays owned here. Surfaces that
   * can act on unmatched free text (analyze what the user typed) need to see
   * it without taking over the controller.
   */
  onSearchChange?: (value: string) => void;
  onSelect: (stock: RouterOutput["stock"]["search_stock"][number]) => void;
  optionEndIcon?: React.ReactNode | ((symbol: string) => React.ReactNode);
  placeholder?: string;
  selectedItems?: readonly T_AutocompleteSelectedItem[];
  selectionMode?: "multiple" | "single";
}

export default function StockSearch({
  appearance = "default",
  className,
  fadingStocks,
  inlineOptions = false,
  onClearSelectedItems,
  onCloseSidebar,
  onSearchChange,
  onSelect,
  optionEndIcon,
  placeholder = "Search stocks…",
  selectedItems = [],
  selectionMode = "single",
}: T_StockSearchProps) {
  const [searchValue, setSearchValue] = React.useState("");
  const [debouncedSearchValue, debouncedState] = useDebounce(searchValue, 300);
  const stocks = trpcApi.stock.search_stock.useQuery({
    searchString: debouncedSearchValue,
  });
  const previousSearchRef = React.useRef("");

  React.useEffect(() => {
    if (
      debouncedSearchValue &&
      debouncedSearchValue !== previousSearchRef.current
    ) {
      trackEvent(MixpanelEvents.SEARCH_PERFORMED, {
        query: debouncedSearchValue,
      });
      previousSearchRef.current = debouncedSearchValue;
    }
  }, [debouncedSearchValue]);

  return (
    <StockSearchView
      appearance={appearance}
      className={className}
      fadingStocks={fadingStocks}
      inlineOptions={inlineOptions}
      isLoading={stocks.isFetching || debouncedState.isPending()}
      items={stocks.data ?? []}
      onClearSelectedItems={onClearSelectedItems}
      onFocus={onCloseSidebar}
      onSearchChange={(value) => {
        setSearchValue(value);
        onSearchChange?.(value);
      }}
      onSelect={(stock) => {
        setSearchValue("");
        onSearchChange?.("");
        trackEvent(MixpanelEvents.SEARCH_RESULT_SELECTED, {
          symbol: stock.symbol,
        });
        onSelect(stock);
      }}
      optionEndIcon={optionEndIcon}
      placeholder={placeholder}
      searchValue={searchValue}
      selectedItems={selectedItems}
      selectionMode={selectionMode}
    />
  );
}
